// src/components/common/ReadOnlyCompensationView.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import employeeService from '../../services/employee.service';
import companySettingsService from '../../services/companySettings.service';
import professionalTaxService from '../../services/professionalTax.service';

const ReadOnlyCompensationView = ({ employeeId }) => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('annual'); // 'annual' or 'monthly'
    const [annualCTC, setAnnualCTC] = useState(0);
    const [salaryComponents, setSalaryComponents] = useState([]);
    const [companyState, setCompanyState] = useState('');

    useEffect(() => {
        fetchCompensation();
    }, [employeeId]);

    const fetchCompensation = async () => {
        try {
            setLoading(true);
            let employee;

            // If viewing own profile (employee/manager portal), use profile endpoint
            // If viewing another employee (admin portal), use getEmployeeById
            if (!employeeId || employeeId === user?.id) {
                employee = await employeeService.getProfile();
            } else {
                const response = await employeeService.getEmployeeById(employeeId);
                employee = response.employee || response; // Extract employee from response wrapper
            }

            // Get company's state for PT calculation (PT is based on organization address, not employee address)
            const companyProfile = await companySettingsService.getCompanyProfile();
            setCompanyState(companyProfile?.state || '');

            if (employee && employee.pay_package) {
                const payPackage = employee.pay_package;
                setAnnualCTC(parseFloat(payPackage.annual_ctc || payPackage.ctc || 0));
                // Strip out Professional Tax from saved components - we'll calculate it dynamically
                const sanitizedComponents = (payPackage.salary_components || []).filter(
                    comp => (comp.name || '').toLowerCase() !== 'professional tax'
                );
                setSalaryComponents(sanitizedComponents);
            } else {
                console.warn('⚠️ No pay_package found for employee');
                setAnnualCTC(0);
                setSalaryComponents([]);
            }
        } catch (error) {
            console.error('❌ Error fetching employee compensation:', error);
            setAnnualCTC(0);
            setSalaryComponents([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateTotals = () => {
        const earnings = salaryComponents
            .filter(c => c.type === 'earning')
            .reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
        const deductions = salaryComponents
            .filter(c => c.type === 'deduction')
            .reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);

        // Calculate monthly gross from earnings for PT calculation
        const monthlyGross = salaryComponents
            .filter(comp => comp.type === 'earning')
            .reduce((sum, comp) => {
                const monthly = comp.monthly_amount ?? (comp.amount ? comp.amount / 12 : 0);
                return sum + parseFloat(monthly || 0);
            }, 0);

        // Calculate Professional Tax dynamically based on company's state (organization address)
        const ptMonthly = professionalTaxService.calculatePTSync(companyState, monthlyGross);
        const ptAnnual = ptMonthly > 0 ? parseFloat((ptMonthly * 12).toFixed(2)) : 0;
        const totalDeductions = deductions + ptAnnual;

        return {
            earnings,
            deductions: totalDeductions,
            net: earnings - totalDeductions,
            ptAnnual,
            ptMonthly
        };
    };

    const totals = calculateTotals();

    if (loading) {
        return <div className="p-6 text-gray-500 dark:text-gray-400">Loading compensation details...</div>;
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Compensation Details</h3>
            </div>

            {/* Annual CTC */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Annual CTC
                </label>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                    ₹{annualCTC.toLocaleString()}
                </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View:</span>
                <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                    <button
                        onClick={() => setViewMode('annual')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            viewMode === 'annual'
                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                : 'text-gray-600 dark:text-gray-400'
                        }`}
                    >
                        Annual
                    </button>
                    <button
                        onClick={() => setViewMode('monthly')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            viewMode === 'monthly'
                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                : 'text-gray-600 dark:text-gray-400'
                        }`}
                    >
                        Monthly
                    </button>
                </div>
            </div>

            {/* Salary Components */}
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Salary Breakdown</h4>
                </div>

                {/* Earnings */}
                <div>
                    <h5 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2">EARNINGS</h5>
                    <div className="space-y-2">
                        {salaryComponents
                            .filter(comp => comp.type === 'earning')
                            .map((comp, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-4 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                                >
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            {comp.name}
                                        </span>
                                        {comp.is_custom && (
                                            <span className="ml-2 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-2 py-1 rounded">
                                                Custom
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                        ₹{((viewMode === 'annual' ? comp.amount : comp.monthly_amount) || 0).toLocaleString()}
                                    </span>
                                </div>
                            ))}
                    </div>
                </div>

                {/* Deductions */}
                {(salaryComponents.some(c => c.type === 'deduction') || totals.ptAnnual > 0) && (
                    <div>
                        <h5 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">DEDUCTIONS</h5>
                        <div className="space-y-2">
                            {salaryComponents
                                .filter(comp => comp.type === 'deduction')
                                .map((comp, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-4 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                                    >
                                        <div className="flex-1">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                {comp.name}
                                            </span>
                                            {comp.is_custom && (
                                                <span className="ml-2 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-2 py-1 rounded">
                                                    Custom
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                                            -₹{((viewMode === 'annual' ? comp.amount : comp.monthly_amount) || 0).toLocaleString()}
                                        </span>
                                    </div>
                                ))}
                            {/* Professional Tax - Auto calculated */}
                            {totals.ptAnnual > 0 && (
                                <div className="flex items-center gap-4 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <div className="flex-1">
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            Professional Tax
                                        </span>
                                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
                                            Auto (by state)
                                        </span>
                                    </div>
                                    <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                                        -₹{(viewMode === 'annual' ? totals.ptAnnual : totals.ptMonthly).toLocaleString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Totals */}
                <div className="border-t border-gray-300 dark:border-gray-600 pt-4 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Total Earnings:</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                            ₹{(viewMode === 'annual' ? totals.earnings : totals.earnings / 12).toLocaleString()}
                        </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Total Deductions:</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">
                            -₹{(viewMode === 'annual' ? totals.deductions : totals.deductions / 12).toLocaleString()}
                        </span>
                    </div>
                    <div className="flex justify-between items-center text-lg font-bold border-t border-gray-300 dark:border-gray-600 pt-2">
                        <span className="text-gray-900 dark:text-white">Net Pay:</span>
                        <span className="text-blue-600 dark:text-blue-400">
                            ₹{(viewMode === 'annual' ? totals.net : totals.net / 12).toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReadOnlyCompensationView;
